import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    useTheme,
    useMediaQuery,
    Grid,
    Paper,
    Divider,
    CircularProgress,
    Alert
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationSettings from './NotificationSettings';
import SupportHelp from './SupportHelp';
import ProfileSettingsNav from './ProfileSettingsNav';
import CompanySettings from './CompanySettings';
import SubscriptionBilling from './SubscriptionBilling';
import { useLocation } from 'react-router-dom';
import { updateProfile, getProfile, sendEmailOtpApi, verifyEmailOtpApi, sendMobileOtpApi, verifyMobileOtpApi } from '../../Api/ProfileApi';

const ProfileSecurity = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showPassword, setShowPassword] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [activeSection, setActiveSection] = useState('profile-security');
    const [generalError, setGeneralError] = useState('');
    const [generalSuccess, setGeneralSuccess] = useState('');
    const location = useLocation();

    // Original loaded profile data
    const [originalData, setOriginalData] = useState({
        email: '',
        phone: ''
    });

    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
        password: '**************'
    });

    // Verification states
    const [emailStatus, setEmailStatus] = useState('idle'); // 'idle', 'sent', 'verified'
    const [emailOtp, setEmailOtp] = useState('');
    const [resendTimerEmail, setResendTimerEmail] = useState(0);
    const [loadingEmailAction, setLoadingEmailAction] = useState('');

    const [mobileStatus, setMobileStatus] = useState('idle'); // 'idle', 'sent', 'verified'
    const [mobileOtp, setMobileOtp] = useState('');
    const [resendTimerMobile, setResendTimerMobile] = useState(0);
    const [loadingMobileAction, setLoadingMobileAction] = useState('');

    // countdown timers
    useEffect(() => {
        let interval;
        if (resendTimerEmail > 0) {
            interval = setInterval(() => setResendTimerEmail(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimerEmail]);

    useEffect(() => {
        let interval;
        if (resendTimerMobile > 0) {
            interval = setInterval(() => setResendTimerMobile(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimerMobile]);

    // Helper function to extract 10 digits from phone number (removes country code)
    const extractPhoneDigits = (phone) => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        return digits.length > 10 ? digits.slice(-10) : digits;
    };

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const response = await getProfile();
                const profile = response?.data ?? response;

                if (profile) {
                    const phoneValue = profile.mobile ?? profile.phone ?? profile.phoneNumber ?? '';
                    const extractedPhone = extractPhoneDigits(phoneValue);
                    const loadedEmail = profile.email ?? '';
                    
                    setOriginalData({
                        email: loadedEmail,
                        phone: extractedPhone
                    });

                    setFormData((prev) => ({
                        ...prev,
                        email: loadedEmail,
                        phone: extractedPhone,
                        firstName: profile.firstName ?? prev.firstName,
                        lastName: profile.lastName ?? prev.lastName
                    }));
                }
            } catch (err) {
                console.error('Error loading profile:', err);
            }
        };

        loadProfile();
    }, [location.state]);

    const handleInputChange = (field) => (event) => {
        const val = event.target.value;
        setFormData({
            ...formData,
            [field]: val
        });
        setGeneralError('');
        setGeneralSuccess('');

        if (field === 'email') {
            // Reset verification status if they revert to original or change email
            if (val.trim().toLowerCase() === originalData.email.toLowerCase()) {
                setEmailStatus('idle');
            } else if (emailStatus === 'verified') {
                setEmailStatus('idle');
            }
            setEmailOtp('');
        }
    };

    const handlePhoneChange = (event) => {
        const value = event.target.value;
        const digitsOnly = value.replace(/\D/g, '').slice(0, 10);

        setFormData({
            ...formData,
            phone: digitsOnly
        });
        setGeneralError('');
        setGeneralSuccess('');

        // Reset verification status if they revert to original or edit phone
        if (digitsOnly === originalData.phone) {
            setMobileStatus('idle');
        } else if (mobileStatus === 'verified') {
            setMobileStatus('idle');
        }
        setMobileOtp('');

        if (digitsOnly.length > 0 && digitsOnly.length !== 10) {
            setPhoneError('Phone number must be exactly 10 digits');
        } else {
            setPhoneError('');
        }
    };

    const handleSendEmailOtp = async () => {
        setGeneralError('');
        setGeneralSuccess('');
        setLoadingEmailAction('send');
        try {
            await sendEmailOtpApi(formData.email.trim());
            setEmailStatus('sent');
            setResendTimerEmail(30);
            setGeneralSuccess('Verification code sent to your email.');
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || error.message || 'Failed to send OTP.';
            setGeneralError(msg);
        } finally {
            setLoadingEmailAction('');
        }
    };

    const handleVerifyEmailOtp = async () => {
        setGeneralError('');
        setGeneralSuccess('');
        setLoadingEmailAction('verify');
        try {
            await verifyEmailOtpApi(formData.email.trim(), emailOtp);
            setEmailStatus('verified');
            setGeneralSuccess('Email verified successfully!');
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || error.message || 'Invalid OTP.';
            setGeneralError(msg);
        } finally {
            setLoadingEmailAction('');
        }
    };

    const handleSendMobileOtp = async () => {
        setGeneralError('');
        setGeneralSuccess('');
        setLoadingMobileAction('send');
        try {
            await sendMobileOtpApi(formData.phone);
            setMobileStatus('sent');
            setResendTimerMobile(30);
            setGeneralSuccess('Verification OTP sent to your phone.');
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || error.message || 'Failed to send OTP.';
            setGeneralError(msg);
        } finally {
            setLoadingMobileAction('');
        }
    };

    const handleVerifyMobileOtp = async () => {
        setGeneralError('');
        setGeneralSuccess('');
        setLoadingMobileAction('verify');
        try {
            await verifyMobileOtpApi(formData.phone, mobileOtp);
            setMobileStatus('verified');
            setGeneralSuccess('Mobile verified successfully!');
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || error.message || 'Invalid OTP.';
            setGeneralError(msg);
        } finally {
            setLoadingMobileAction('');
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleEditPassword = () => {
        setIsEditingPassword(true);
        setFormData({
            ...formData,
            password: ''
        });
    };

    const handleSave = async () => {
        // Enforce verification checks if field changed
        const isEmailChanged = formData.email.trim().toLowerCase() !== originalData.email.toLowerCase();
        if (isEmailChanged && emailStatus !== 'verified') {
            setGeneralError('Please verify your new email address first.');
            return;
        }

        const isPhoneChanged = formData.phone !== originalData.phone;
        if (isPhoneChanged && mobileStatus !== 'verified') {
            setGeneralError('Please verify your new phone number first.');
            return;
        }

        if (formData.phone && formData.phone.length !== 10) {
            setPhoneError('Phone number must be exactly 10 digits');
            return;
        }

        const payload = {
            email: formData.email.trim(),
            mobile: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
            ...(isEditingPassword && formData.password && formData.password !== '**************'
                ? { password: formData.password }
                : {})
        };

        try {
            setIsSaving(true);
            setGeneralError('');
            setGeneralSuccess('');
            const response = await updateProfile(payload);
            const updatedData = response?.data ?? response;

            const phoneValue = updatedData?.mobile ?? updatedData?.phone ?? formData.phone;
            const extractedPhone = extractPhoneDigits(phoneValue);
            const loadedEmail = updatedData?.email ?? formData.email;

            setOriginalData({
                email: loadedEmail,
                phone: extractedPhone
            });

            setFormData((prev) => ({
                ...prev,
                email: loadedEmail,
                phone: extractedPhone,
                firstName: updatedData?.firstName ?? prev.firstName,
                lastName: updatedData?.lastName ?? prev.lastName,
                ...(isEditingPassword ? { password: '**************' } : {})
            }));

            setPhoneError('');
            setIsEditingPassword(false);
            setEmailStatus('idle');
            setMobileStatus('idle');
            setGeneralSuccess('Profile updated successfully!');
        } catch (error) {
            console.error('Failed to update profile:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to update profile.';
            setGeneralError(msg);
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#F8F9FA', minHeight: 'calc(100vh - 60px)', maxWidth: '1400px', mx: 'auto' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>Settings</Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 }, alignItems: { xs: 'stretch', md: 'flex-start' } }}>
                {/* Left Sidebar - Navigation */}
                <Box sx={{ width: { xs: '100%', md: '240px' }, flexShrink: 0 }}>
                    <ProfileSettingsNav activeSection={activeSection} onSectionChange={setActiveSection} />
                </Box>
 
                {/* Right Content Area - Dynamic Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Paper elevation={0}
                        sx={{ p: { xs: 2.5, sm: 3, md: 4 }, borderRadius: '12px', border: '1px solid #E5E7EB', bgcolor: 'background.paper' }}
                    >
                        {activeSection === 'profile-security' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {generalError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{generalError}</Alert>
                                )}
                                {generalSuccess && (
                                    <Alert severity="success" sx={{ mb: 2 }}>{generalSuccess}</Alert>
                                )}
 
                                {/* Mail ID and Phone Number in Grid */}
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <EmailOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                                    Mail ID <span style={{ color: 'red' }}>*</span>
                                                </Typography>
                                                {emailStatus === 'verified' && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#E6F4EA', color: '#137333', px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 500 }}>
                                                        <CheckCircleIcon sx={{ fontSize: 12 }} /> Verified
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={formData.email}
                                                    onChange={handleInputChange('email')}
                                                    variant="outlined"
                                                    disabled={loadingEmailAction !== '' || emailStatus === 'verified'}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '4px',
                                                            bgcolor: 'background.paper'
                                                        }
                                                    }}
                                                />
                                                {formData.email.trim().toLowerCase() !== originalData.email.toLowerCase() && emailStatus === 'idle' && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleSendEmailOtp}
                                                        disabled={loadingEmailAction !== '' || !formData.email.trim() || !formData.email.includes('@')}
                                                    >
                                                        {loadingEmailAction === 'send' ? <CircularProgress size={16} sx={{ color: '#6B31C4' }} /> : 'Verify Email'}
                                                    </Button>
                                                )}
                                            </Box>

                                            {/* Email OTP Verification Field */}
                                            {emailStatus === 'sent' && (
                                                <Box sx={{ mt: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
                                                    <Typography variant="body2" sx={{ mb: 1, color: '#4b5563', fontSize: '12px' }}>
                                                        Enter the OTP code sent to your email:
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                        <TextField
                                                            fullWidth size="small" placeholder="Enter OTP"
                                                            value={emailOtp}
                                                            onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                            disabled={loadingEmailAction !== ''}
                                                            sx={{ backgroundColor: 'white' }}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleVerifyEmailOtp}
                                                            disabled={loadingEmailAction !== '' || emailOtp.length < 4}
                                                            sx={{ minWidth: '100px', backgroundColor: '#6B31C4', color: 'white', '&:hover': { backgroundColor: '#5a27a8' } }}
                                                        >
                                                            {loadingEmailAction === 'verify' ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Verify'}
                                                        </Button>
                                                    </Box>
                                                    <Button
                                                        variant="text"
                                                        onClick={handleSendEmailOtp}
                                                        disabled={loadingEmailAction !== '' || resendTimerEmail > 0}
                                                        sx={{ mt: 1, color: '#6B31C4', fontSize: '12px', textTransform: 'none' }}
                                                    >
                                                        {resendTimerEmail > 0 ? `Resend in ${resendTimerEmail}s` : 'Resend Code'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <PhoneOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                                    Phone Number
                                                </Typography>
                                                {mobileStatus === 'verified' && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#E6F4EA', color: '#137333', px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 500 }}>
                                                        <CheckCircleIcon sx={{ fontSize: 12 }} /> Verified
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={formData.phone}
                                                    onChange={handlePhoneChange}
                                                    variant="outlined"
                                                    disabled={loadingMobileAction !== '' || mobileStatus === 'verified'}
                                                    inputProps={{
                                                        maxLength: 10,
                                                        inputMode: 'numeric',
                                                        pattern: '[0-9]{10}'
                                                    }}
                                                    error={!!phoneError}
                                                    helperText={phoneError}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '4px',
                                                            bgcolor: 'background.paper'
                                                        }
                                                    }}
                                                />
                                                {formData.phone !== originalData.phone && mobileStatus === 'idle' && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleSendMobileOtp}
                                                        disabled={loadingMobileAction !== '' || formData.phone.length !== 10}
                                                    >
                                                        {loadingMobileAction === 'send' ? <CircularProgress size={16} sx={{ color: '#6B31C4' }} /> : 'Verify Mobile'}
                                                    </Button>
                                                )}
                                            </Box>

                                            {/* Mobile OTP Verification Field */}
                                            {mobileStatus === 'sent' && (
                                                <Box sx={{ mt: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
                                                    <Typography variant="body2" sx={{ mb: 1, color: '#4b5563', fontSize: '12px' }}>
                                                        Enter the OTP code sent to your mobile:
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                        <TextField
                                                            fullWidth size="small" placeholder="Enter OTP"
                                                            value={mobileOtp}
                                                            onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                            disabled={loadingMobileAction !== ''}
                                                            sx={{ backgroundColor: 'white' }}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleVerifyMobileOtp}
                                                            disabled={loadingMobileAction !== '' || mobileOtp.length < 4}
                                                            sx={{ minWidth: '100px', backgroundColor: '#6B31C4', color: 'white', '&:hover': { backgroundColor: '#5a27a8' } }}
                                                        >
                                                            {loadingMobileAction === 'verify' ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Verify'}
                                                        </Button>
                                                    </Box>
                                                    <Button
                                                        variant="text"
                                                        onClick={handleSendMobileOtp}
                                                        disabled={loadingMobileAction !== '' || resendTimerMobile > 0}
                                                        sx={{ mt: 1, color: '#6B31C4', fontSize: '12px', textTransform: 'none' }}
                                                    >
                                                        {resendTimerMobile > 0 ? `Resend in ${resendTimerMobile}s` : 'Resend Code'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* First Name and Last Name in Grid */}
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <PersonOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}                                          >
                                                    First Name
                                                </Typography>
                                            </Box>
                                            <TextField fullWidth size="small" value={formData.firstName} onChange={handleInputChange('firstName')} variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': { borderRadius: '4px', bgcolor: 'background.paper' }
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px', mb: 1 }} >
                                                Last Name
                                            </Typography>
                                            <TextField fullWidth size="small"
                                                value={formData.lastName} onChange={handleInputChange('lastName')} variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px', bgcolor: 'background.paper' } }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Change Password */}
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}                            >
                                                    Change Password
                                                </Typography>
                                            </Box>
                                            <TextField
                                                fullWidth size="small" type={showPassword ? 'text' : 'password'}
                                                value={formData.password} onChange={handleInputChange('password')}
                                                variant="outlined" disabled={!isEditingPassword} placeholder={isEditingPassword ? 'Enter new password' : ''}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '4px', bgcolor: 'background.paper',
                                                        '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                {!isEditingPassword && (
                                                                    <IconButton onClick={handleEditPassword} edge="end" sx={{ color: 'text.secondary' }} size="small"                         >
                                                                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                                                    </IconButton>
                                                                )}
                                                                <IconButton onClick={handleTogglePasswordVisibility} edge="end" sx={{ color: 'text.secondary' }} size="small"  >
                                                                    {showPassword ? (
                                                                        <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                                                                    ) : (
                                                                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                                                    )}
                                                                </IconButton>
                                                            </Box>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Save Button */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                                        {isSaving ? 'Saving...' : 'Confirm & Save'}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {activeSection === 'notifications' && (
                            <NotificationSettings />
                        )}

                        {activeSection === 'subscription' && (
                            <SubscriptionBilling />
                        )}

                        {activeSection === 'support' && (
                            <SupportHelp />
                        )}

                        {activeSection === 'company-settings' && (
                            <CompanySettings />
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default ProfileSecurity;
